import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CONFIG } from '@cpd-app/configuration';
import { Chart, ChartOptions, ChartType } from 'chart.js';
import { MatchQuality, MatchScores } from '@cpd-app/core-module/models';
import { Subscription, debounceTime, fromEvent } from 'rxjs';

enum SectorEnum {
  Lifestyle = 0,
  Values = 1,
  Interests = 2,
  Skills = 3
}

@Component({
  selector: 'cpd-match-score-chart',
  templateUrl: './match-score-chart.component.html',
  styleUrls: ['./match-score-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MatchScoreChartComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('scoresChart', { static: true }) public scoresChart: ElementRef;

  @Input() public data: MatchScores;
  @Input() public drawOuterRing = true;
  @Input() public resizable = false;
  @Input() public colorMissing = 'rgb(240, 240, 240)';

  public theChart: Chart;

  private colorInterestsStartingMatch = 'rgb(211, 215, 204)';
  private colorInterests = 'rgba(96, 126, 55, 1)';
  private colorValuesStartingMatch = 'rgb(213, 205, 185)';
  private colorValues = 'rgba(142, 112, 42, 1)';
  private colorLifestyleStartingMatch = 'rgb(244, 219, 245)';
  private colorLifestyle = 'rgba(113, 82, 152, 1)';
  private colorSkillsStartingMatch = 'rgb(188, 196, 206)';
  private colorSkills = 'rgba(53, 94, 147, 1)';
  private chartType: ChartType = 'polarArea';
  private chartGap = 1.5;
  private chartLabels: string[] = [
    'lifestyle',
    'values',
    'interests',
    'skills'
  ];
  private backGroundData: number[] = [100, 100, 100, 100];

  private chartOptions: ChartOptions = {
    aspectRatio: 1,
    maintainAspectRatio: true,
    animation: false,
    responsive: true,
    layout: {
      padding: {
        left: 5,
        right: 5,
        top: 5,
        bottom: 5
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        ticks: {
          stepSize: 33,
          display: false
        },
        min: 0,
        max: 100
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false,
        titleFont: {
          size: 9
        },
        titleColor: 'black',
        bodyFont: {
          size: 9
        },
        bodyColor: 'black',
        backgroundColor: 'white',
        borderColor: 'lightgray',
        borderWidth: 1,
        cornerRadius: 5,
        callbacks: {
          label(tooltipItem) {
            return `${this.chart.data.labels[tooltipItem.dataIndex]}: ${
              tooltipItem.element.y
            }%`;
          }
        }
      }
    }
  };

  private resize$ = fromEvent(window, 'resize').pipe(debounceTime(700));
  private sub = new Subscription();

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.data?.currentValue &&
      !changes.data?.firstChange &&
      this.isDiffMatchScore(
        changes.data.currentValue,
        changes.data.previousValue
      )
    ) {
      this.updateChage();
    }
  }

  public ngAfterViewInit(): void {
    if (this.resizable) {
      this.sub = this.resize$.subscribe(() => {
        this.theChart.resize();
      });
    }

    this.initChart();
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  public initChart(): void {
    const chartContext = (<HTMLCanvasElement>(
      this.scoresChart.nativeElement
    )).getContext('2d');

    const chartData = this.calculateChartData(this.data);
    const chartColors = this.calculateChartColors(this.data);

    this.theChart = new Chart(chartContext, {
      type: this.chartType,
      data: {
        labels: this.chartLabels,
        datasets: [
          {
            data: chartData,
            backgroundColor: chartColors,
            borderWidth: this.chartGap,
            borderColor: 'white'
          },
          {
            data: this.backGroundData,
            backgroundColor: this.colorMissing,
            borderColor: 'white'
          }
        ]
      },
      options: this.chartOptions,
      plugins: [
        {
          id: 'occupation-chart-radar',
          afterDraw: this.charAfterDraw.bind(this)
        }
      ]
    });
  }

  private updateChage(): void {
    if (!this.theChart) {
      return;
    }

    this.theChart.data.datasets[0].data = this.calculateChartData(this.data);
    this.theChart.data.datasets[0].backgroundColor = this.calculateChartColors(
      this.data
    );

    this.theChart.update();
  }

  private calculateChartColors(data: MatchScores): string[] {
    const chartColors = [];

    const startingMatchId = CONFIG.OCCUPATION.MATCH_QUALITY.find(
      (x) => x.isStartingMatch === true
    ).id;

    chartColors[SectorEnum.Lifestyle] = !data.lifestyleMatchQualityId
      ? this.colorMissing
      : data.lifestyleMatchQualityId === startingMatchId
        ? this.colorLifestyleStartingMatch
        : this.colorLifestyle;

    chartColors[SectorEnum.Values] = !data.valuesMatchQualityId
      ? this.colorMissing
      : data.valuesMatchQualityId === startingMatchId
        ? this.colorValuesStartingMatch
        : this.colorValues;

    chartColors[SectorEnum.Interests] = !data.interestsMatchQualityId
      ? this.colorMissing
      : data.interestsMatchQualityId === startingMatchId
        ? this.colorInterestsStartingMatch
        : this.colorInterests;

    chartColors[SectorEnum.Skills] = !data.skillsMatchQualityId
      ? this.colorMissing
      : data.skillsMatchQualityId === startingMatchId
        ? this.colorSkillsStartingMatch
        : this.colorSkills;

    return chartColors;
  }

  private calculateChartData(data: MatchScores): number[] {
    return [
      this.normalizeScore(data.lifestyleMatchQualityId),
      this.normalizeScore(data.valuesMatchQualityId),
      this.normalizeScore(data.interestsMatchQualityId),
      this.normalizeScore(data.skillsMatchQualityId)
    ];
  }

  // this weirdness is to show fixed values instead of the actual scores so the chart would look better
  private normalizeScore(quality: number): number {
    const normalValues = [
      { quality: null, value: 0 },
      { quality: MatchQuality.NA, value: 0 },
      { quality: MatchQuality.Poor, value: 20 },
      { quality: MatchQuality.Fair, value: 40 },
      { quality: MatchQuality.Good, value: 60 },
      { quality: MatchQuality.Great, value: 80 },
      { quality: MatchQuality.Excellent, value: 100 },
      { quality: MatchQuality.Overqualified, value: 100 }
    ];

    const normalizedValue = normalValues.find((x) => x.quality === quality);

    return normalizedValue?.value || 0;
  }

  private charAfterDraw(chart, _easing): void {
    const { ctx, scales } = chart;
    const { xCenter, yCenter, drawingArea: radius } = scales.r;

    ctx.lineWidth = this.chartGap;
    ctx.strokeStyle = 'white';

    // draw white circles so that the solid color of the sectors is cut and look like a target
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(xCenter, yCenter, (radius * i) / 3, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // draw the gray thin circle around the entire chart
    if (this.drawOuterRing) {
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(223, 223, 223, 0.5)';

      ctx.beginPath();
      ctx.arc(xCenter, yCenter, radius + 5, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // draw the red arc denoting overqualified
    if (this.data?.isOverqualified || this.data?.skillsMatchQualityId === 6) {
      this.drawColoredArc(
        ctx,
        xCenter,
        yCenter,
        radius,
        SectorEnum.Skills,
        'rgb(204, 78, 0)'
      );
    }

    const excellentMarker = CONFIG.OCCUPATION.MATCH_QUALITY.find(
      (x) => x.name === 'Excellent'
    ).id;

    // excellent skills match
    if (
      !this.data.isOverqualified &&
      this.data.skillsMatchQualityId === excellentMarker
    ) {
      this.drawColoredArc(
        ctx,
        xCenter,
        yCenter,
        radius,
        SectorEnum.Skills,
        'rgb(45, 105, 178)'
      );
    }

    // excellent lifestyle match
    if (
      !this.data.isOverqualified &&
      this.data.lifestyleMatchQualityId === excellentMarker
    ) {
      this.drawColoredArc(
        ctx,
        xCenter,
        yCenter,
        radius,
        SectorEnum.Lifestyle,
        'rgba(113, 82, 152, 0.8)'
      );
    }

    // excellent interests match
    if (
      !this.data.isOverqualified &&
      this.data.interestsMatchQualityId === excellentMarker
    ) {
      this.drawColoredArc(
        ctx,
        xCenter,
        yCenter,
        radius,
        SectorEnum.Interests,
        'rgb(132, 183, 43)'
      );
    }

    // excellent work values match
    if (
      !this.data.isOverqualified &&
      this.data.valuesMatchQualityId === excellentMarker
    ) {
      this.drawColoredArc(
        ctx,
        xCenter,
        yCenter,
        radius,
        SectorEnum.Values,
        'rgb(196, 153, 55)'
      );
    }
  }

  private drawColoredArc(
    ctx: any,
    xCenter: number,
    yCenter: number,
    radius: number,
    sector: number,
    color: string
  ): void {
    const extraBandWidth = 4;
    const startingAngle = (sector * 2 * Math.PI) / 4 - 0.5 * Math.PI;

    ctx.lineWidth = extraBandWidth;
    ctx.strokeStyle = color;

    ctx.beginPath();
    ctx.arc(
      xCenter,
      yCenter,
      radius + 1 + extraBandWidth / 2,
      startingAngle,
      startingAngle + (2 * Math.PI) / 4
    );
    ctx.stroke();
  }

  private isDiffMatchScore(
    oldValue: MatchScores,
    newValue: MatchScores
  ): boolean {
    return (
      oldValue.skillsMatchQualityId !== newValue.skillsMatchQualityId ||
      oldValue.valuesMatchQualityId !== newValue.valuesMatchQualityId ||
      oldValue.interestsMatchQualityId !== newValue.interestsMatchQualityId ||
      oldValue.lifestyleMatchQualityId !== newValue.lifestyleMatchQualityId ||
      oldValue.isOverqualified !== newValue.isOverqualified
    );
  }
}
